import React, { Component } from 'react';
import { reduxForm, formValues } from 'redux-form';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';
import { first, last, sortBy } from 'lodash';

import Header from '../../../containers/purchase-dashboard/autopilot/header.container';
import MultiCurrencyNotice from '../../../containers/multi-currency-notice.container';
import PBGUserAgreement from '../../pbg-user-agreement';
import Refund from '../../../containers/purchase-dashboard/autopilot/refund.container';
import ShareSummary from '../../../containers/purchase-dashboard/autopilot/share-summary.container';
import TermsAndConditions from '../../../containers/terms-and-conditions-link';
import TriggerFields from '../../../containers/purchase-dashboard/autopilot/trigger-fields.container';
import Alert from '../../alert';
import { formatMessage } from '../../../lib/intl';

import '../../../css/autopilot.css';

const i18n = defineMessages({
  triggerOutsideOfRange: {
    defaultMessage: 'Trigger level is outside the allowed range for this purchase',
    id: 'autopilot.errors.triggerOutsideOfRange',
  },
  triggerIsLessThanMin: {
    id: 'autoPilot.errors.triggerIsLessThanMin',
    defaultMessage: 'Trigger level is less than the minimum you set. Make Changes to update that setting.',
  },
  triggerIsMoreThanMax: {
    id: 'autoPilot.errors.triggerIsMoreThanMax',
    defaultMessage: 'Trigger level is more than the maximum you set. Make Changes to update that setting.',
  },

  lessThanCommittedSlots: {
    id: 'autoPilot.errors.lessThanCommittedSlots',
    defaultMessage: 'Cannot be less than the current committed {slotsName} for the group.',
  },
  lessThanCommittedSlots_adult: {
    id: 'autoPilot.errors.lessThanCommittedSlots_adult',
    defaultMessage: 'Cannot be less than the current committed adults for the group.',
  },
  lessThanCommittedSlots_passenger: {
    id: 'autoPilot.errors.lessThanCommittedSlots_passenger',
    defaultMessage: 'Cannot be less than the current committed passengers for the group.',
  },
  lessThanCommittedSlots_ticket: {
    id: 'autoPilot.errors.lessThanCommittedSlots_ticket',
    defaultMessage: 'Cannot be less than the current committed tickets for the group.',
  },
  lessThanCommittedSlots_seat: {
    id: 'autoPilot.errors.lessThanCommittedSlots_seat',
    defaultMessage: 'Cannot be less than the current committed seats for the group.',
  },
  lessThanCommittedSlots_item: {
    id: 'autoPilot.errors.lessThanCommittedSlots_item',
    defaultMessage: 'Cannot be less than the current committed items for the group.',
  },
  lessThanCommittedSlots_shirt: {
    id: 'autoPilot.errors.lessThanCommittedSlots_shirt',
    defaultMessage: 'Cannot be less than the current committed shirts for the group.',
  },
  lessThanCommittedSlots_person: {
    id: 'autoPilot.errors.lessThanCommittedSlots_person',
    defaultMessage: 'Cannot be less than the current committed people for the group.',
  },
  lessThanCommittedSlots_guest: {
    id: 'autoPilot.errors.lessThanCommittedSlots_guest',
    defaultMessage: 'Cannot be less than the current committed guests for the group.',
  },
  lessThanCommittedSlots_player: {
    id: 'autoPilot.errors.lessThanCommittedSlots_player',
    defaultMessage: 'Cannot be less than the current committed players for the group.',
  },
  lessThanCommittedSlots_student: {
    id: 'autoPilot.errors.lessThanCommittedSlots_student',
    defaultMessage: 'Cannot be less than the current committed students for the group.',
  },
});

class Autopilot extends Component {
  constructor(props) {
    super(props);
    this.onSubmit = this.onSubmit.bind(this);
  }

  onSubmit(data) {
    return this.props.onSubmit(data);
  }

  get merchantName() {
    return this.props.purchase.merchant.displayName;
  }

  renderAlert() {
    const { error } = this.props;

    if (!error) return null;

    return (
      <Alert type="warning" className="alert-form-top">
        <i className="fa fa-fw fa-ban" />
        {error}
      </Alert>
    );
  }

  render() {
    const { handleSubmit } = this.props;
    const ShareSummaryWithTriggerSlots = formValues('autoPilotTriggerSlots')(ShareSummary);
    return (
      <div>
        <Header />
        {this.renderAlert()}
        <form onSubmit={handleSubmit(this.onSubmit)}>
          <div className="divider pb-3">
            <div className="mb-4 text-center">
              <TriggerFields />
            </div>
            <ShareSummaryWithTriggerSlots />
            <Refund />
            <MultiCurrencyNotice />
          </div>
          <div className="row mt-3">
            <div className="col-7">
              <p className="small-print">
                <FormattedMessage
                  id="autoPilot.acceptTOS"
                  defaultMessage='By clicking "Activate auto-pilot"
                    you confirm that you accept the {ua} and {merchant} {tos}'
                  values={{
                    ua: <PBGUserAgreement />,
                    merchant: this.merchantName,
                    tos: <TermsAndConditions />,
                  }}
                />
              </p>
            </div>
            <div className="col-5">
              <button type="submit" className="btn btn-primary float-right">
                <FormattedMessage
                  id="autoPilot.submit"
                  defaultMessage="Activate auto-pilot"
                />
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}

export const FORM_NAME = 'autopilotForm';

const validate = ({ autoPilotTriggerSlots: value }, { purchase, intl }) => {
  const errors = {};
  const { group } = purchase;

  if (!withinProductCostRange(value, purchase.costRange)) {
    errors.autoPilotTriggerSlots = formatMessage(intl)(i18n.triggerOutsideOfRange);
    return errors;
  }

  if (value < purchase.committedSlots) {
    errors.autoPilotTriggerSlots = formatMessage(intl)(i18n.lessThanCommittedSlots, purchase.slotName);
    return errors;
  }

  if (group.minSlots && value < group.minSlots) {
    errors.autoPilotTriggerSlots = formatMessage(intl)(i18n.triggerIsLessThanMin);
    return errors;
  }

  if (group.maxSlots && value > group.maxSlots) {
    errors.autoPilotTriggerSlots = formatMessage(intl)(i18n.triggerIsMoreThanMax);
    return errors;
  }

  return errors;
};

export const withinProductCostRange = (slots, costRange) => {
  const range = sortBy(costRange, 'minSlots');
  return slots >= (first(range).minSlots || 0) && slots <= (last(range).maxSlots || 999);
};

export default injectIntl(reduxForm({ form: FORM_NAME, validate })(Autopilot));




// WEBPACK FOOTER //
// ./src/desktop/components/purchase-dashboard/autopilot/index.js